// ----------------------------------------------------------------------------
// Value formatting helpers
// ----------------------------------------------------------------------------
const NA = "NA";

const naText = (value) => {
    if (value === null || value === undefined) return NA;

    if (typeof value === "string") {
        const text = value.trim();
        if (!text) return NA;

        return text
            // Currency
            .replace(/₹/g, "INR ")

            // Dashes
            .replace(/[–—−]/g, "-")

            // Quotes
            .replace(/[‘’]/g, "'")
            .replace(/[“”]/g, '"')

            // Ellipsis
            .replace(/…/g, "...")

            // Bullet
            .replace(/•/g, "-")

            // Non-breaking space
            .replace(/\u00A0/g, " ")

            // Common mojibake (UTF-8 shown as ANSI)
            .replace(/â‚¹/g, "INR ")
            .replace(/â€“/g, "-")
            .replace(/â€”/g, "-")
            .replace(/â€˜/g, "'")
            .replace(/â€™/g, "'")
            .replace(/â€œ/g, '"')
            .replace(/â€/g, '"')
            .replace(/â€¦/g, "...")
            .replace(/Â/g, "");
    }

    if (Array.isArray(value) && value.length === 0) return NA;

    return value;
};

const naJoin = (arr, separator = ", ") => {
    if (!arr || !arr.length) return NA;

    const filtered = arr
        .filter(Boolean)
        .map((item) => naText(item));

    return filtered.length
        ? filtered.join(separator)
        : NA;
};

const naBoolean = (value) => {
    if (value === true || value === "yes" || value === "true") return "Yes";
    if (value === false || value === "no" || value === "false") return "No";
    return NA;
};

const naLink = (url) => {
    if (!url) return NA;
    return url.startsWith("http") ? url : `https://${url}`;
};

// const naSnapchat = (username) => {
//     if (!username) return NA;
//     const cleanUsername = username
//         .replace(/^@/, "")
//         .replace("https://www.snapchat.com/add/", "")
//         .replace("http://www.snapchat.com/add/", "")
//         .trim();
//     return cleanUsername ? `https://www.snapchat.com/add/${cleanUsername}` : NA;
// };
const naSnapchat = (value) => {
    if (!value) return NA;

    const input = String(value).trim();

    // Already a full URL
    if (/^https?:\/\//i.test(input)) {
        return input;
    }

    // Username
    const username = input
        .replace(/^@/, "")
        .replace(/^https?:\/\/(www\.)?snapchat\.com\/add\//i, "")
        .trim();

    return username
        ? `https://www.snapchat.com/add/${username}`
        : NA;
};

// imageUrl is passed in explicitly so this helper stays independent of scope
const naFiles = (files, imageUrl) => {
    if (!files || !files.length) return NA;

    return files
        .map((f) => {
            const filename =
                typeof f === "object" && f !== null
                    ? f.filename || ""
                    : f;

            // return filename ? `${imageUrl}${filename}` : "";
            return filename
                ? `${imageUrl}${encodeURIComponent(decodeURIComponent(filename))}`
                : "";
        })
        .filter(Boolean)
        .join("\r\n\r\n");
};

// ----------------------------------------------------------------------------
// Condition helpers — mirror the Drawer JSX's gating logic exactly, but take
// `record` (the export row) instead of `data` (the drawer's selected record)
// ----------------------------------------------------------------------------
const isBreakthroughTrack = (r) => r?.application_track?.toLowerCase() === "breakthrough";
const isBrandTrack = (r) => r?.application_track === "brand";
const isDesignTrack = (r) => r?.application_track === "design";
const isGrowSubTrack = (r) => isBrandTrack(r) && r?.brand_track === "grow";
const isImagineSubTrack = (r) => isBrandTrack(r) && r?.brand_track === "imagine";

const isInstitutionOrgType = (r) =>
    r?.organisation_type === "University / Research Institution / Academic Lab" ||
    r?.organisation_type === "Pre-commercial spin-out from a university or research institution";

const isCompanyOrgType = (r) =>
    [
        "Startup (incorporated, under 3 years)",
        "Company (registered entity, 3+ years)",
        "Fragrance House",
        "Other",
    ].includes(r?.organisation_type);

const isVisionaryEligible = (r) => r?.has_woman_founder === true && r?.visionary_opt_out === false;

// ----------------------------------------------------------------------------
// Review Highlights helpers — mirror the new admin-only summary block exactly
// (isBrand / brandTrack / isGrow / isImagine / hasBrandTrack / fundingOptions
// / fundingOther / fundingDetails from your latest Index.js)
// ----------------------------------------------------------------------------
const rBrandTrack = (r) => r?.brand_track?.toLowerCase() || "";
const rIsGrow = (r) => rBrandTrack(r) === "grow";
const rIsImagine = (r) => rBrandTrack(r) === "imagine";
const rHasBrandTrack = (r) => ["grow", "imagine"].includes(rBrandTrack(r));
const rFundingOptions = (r) =>
    rIsGrow(r) ? r.grow_external_funding || [] : rIsImagine(r) ? r.imagine_external_funding || [] : [];
const rFundingOther = (r) =>
    rIsGrow(r) ? r.grow_external_funding_other : r.imagine_external_funding_other;
// const rFundingDetails = (r) => (rIsImagine(r) ? r.imagine_external_funding_details : "");
// Review Highlights only renders at all when isBrand(r) && r.brand_track is truthy
const showsReviewHighlights = (r) =>
    (isBrandTrack(r) && !!r?.brand_track) ||
    isDesignTrack(r) ||
    isBreakthroughTrack(r);

// ----------------------------------------------------------------------------
// EXPORT FIELDS — this replaces your existing `const fields = {...}`
// ----------------------------------------------------------------------------
export const createExportFields = ({
    helper,
    imageUrl,
    formatTrack,
}) => {

    return {
        // ============================
        // Review Highlights (admin-only summary block, shown above Step 1)
        // ============================
        review_application_type: {
            header: "Primary Category",
            formatter: (_v, r) => {
                if (!showsReviewHighlights(r)) return NA;

                if (isBrandTrack(r)) {
                    if (!rHasBrandTrack(r)) return "Not Selected";
                    return rIsGrow(r) ? "Grow" : "Imagine";
                }

                if (isDesignTrack(r)) {
                    return "Design";
                }

                if (isBreakthroughTrack(r)) {
                    return "Breakthrough";
                }

                return NA;
            },
        },
        review_external_funding: {
            header: "External Funding",
            formatter: (_v, r) =>
                isBrandTrack(r) &&
                    (rIsGrow(r) || rIsImagine(r)) &&
                    rFundingOptions(r).length
                    ? naJoin(rFundingOptions(r))
                    : NA,
        },
        review_other_funding_details: {
            header: "Other Funding Details",
            formatter: (_v, r) =>
                isBrandTrack(r) &&
                    (rIsGrow(r) || rIsImagine(r))
                    ? naText(rFundingOther(r))
                    : NA,
        },
        review_funding_amount_investor_date: {
            header: "Funding Amount / Investor / Date",
            formatter: (_v, r) =>
                isBrandTrack(r) &&
                    (rIsGrow(r) || rIsImagine(r))
                    ? naText(rFundingOther(r))
                    : NA,
        },
        review_product_samples_available: {
            header: "Product Samples Available",
            formatter: (_v, r) =>
                isBrandTrack(r) && rIsGrow(r)
                    ? naBoolean(r.grow_product_samples)
                    : NA,
        },
        review_visionary_award_eligibility: {
            header: "Visionary Award Eligibility",
            formatter: (_v, r) => {
                if (!showsReviewHighlights(r)) return NA;
                if (r.has_woman_founder !== true) return "Not Eligible (No Woman Founder)";
                if (r.visionary_opt_out === true) return "Not Eligible (Opted Out)";
                return "Eligible";
            },
        },
        review_design_finalist_availability: {
            header: "Finalist Availability",
            formatter: (_v, r) =>
                isDesignTrack(r)
                    ? naText(r.design_finalist_availability)
                    : NA,
        },

        // ============================
        // Step 1 - Information
        // ============================
        first_name: {
            header: "First Name",
            formatter: (_v, r) => naText(r.first_name),
        },
        last_name: {
            header: "Last Name",
            formatter: (_v, r) => naText(r.last_name),
        },
        email: {
            header: "Email",
            formatter: (_v, r) => naText(r.email),
        },
        // phone: {
        //     header: "Phone",
        //     formatter: (_v, r) => {
        //         const val = `${r.country_code || ""}${r.phone || ""}`.trim();
        //         return val ? val : NA;
        //     },
        // },

        phone: {
            header: "Phone",
            formatter: (_v, r) => {
                if (!r.phone) return NA;

                const phone = String(r.phone).trim();
                const code = String(r.country_code || "").trim();

                return code
                    ? `="${code} ${phone}"`
                    : `="${phone}"`;
            },
        },
        title: {
            header: "Title / Designation",
            formatter: (_v, r) => naText(r.title),
        },
        is_university_student: {
            header: "Are you currently a university student?",
            formatter: (_v, r) => naBoolean(r.is_university_student),
        },
        university_name: {
            header: "University Name",
            formatter: (_v, r) => {
                if (r.is_university_student !== true && r.is_university_student !== "yes") return NA;
                return naText(r.university_name);
            },
        },
        has_co_founders: {
            header: "Do you have a co-founder or co-founders?",
            formatter: (_v, r) => naBoolean(r.has_co_founders),
        },
        co_founders: {
            header: "Co-Founders",
            formatter: (_v, r) => {
                if (r.has_co_founders !== true || !r.co_founders?.length) return NA;
                return r.co_founders
                    .map(
                        (cf, i) => [
                            `Co-Founder ${i + 1}`,
                            `Name: ${naText(cf?.full_name)}`,
                            `Role / Title: ${naText(cf?.designation)}`,
                            `Email: ${naText(cf?.email)}`
                        ].join("\n")
                    )
                    .join("\n\n--------------------\n\n");
            },
        },
        has_woman_founder: {
            header: "Do you have a woman founder on your team?",
            formatter: (_v, r) => naBoolean(r.has_woman_founder),
        },
        company_name: {
            header: "Company Name",
            formatter: (_v, r) => naText(r.company_name),
        },
        has_different_brand_name: {
            header: "Different Brand Name",
            formatter: (_v, r) => naBoolean(r.has_different_brand_name),
        },
        brand_name: {
            header: "Brand Name",
            formatter: (_v, r) => {
                if (r.has_different_brand_name !== true && r.has_different_brand_name !== "yes") return NA;
                return naText(r.brand_name);
            },
        },
        entity_type: {
            header: "What type of entity are you?",
            formatter: (_v, r) => naText(r.entity_type),
        },
        // gst_no: {
        //     header: "GST Number",
        //     formatter: (_v, r) => naText(r.gst_no),
        // },
        gst_no: {
            header: "GST Number",
            formatter: (_v, r) => {
                if (!r.gst_no) return NA;
                return `="${r.gst_no}"`;
            },
        },
        company_start_year: {
            header: "Year Business Started",
            formatter: (_v, r) => naText(r.company_start_year),
        },
        employee_count: {
            header: "Number of Employees?",
            formatter: (_v, r) => {
                if (!r.employee_count) return NA;
                return `="${r.employee_count}"`;
            },
        },
        country: {
            header: "Country",
            formatter: (_v, r) => naText(r.country),
        },
        state: {
            header: "State",
            formatter: (_v, r) => naText(r.state),
        },
        city: {
            header: "City",
            formatter: (_v, r) => naText(r.city),
        },
        street_address: {
            header: "Street Address",
            formatter: (_v, r) => naText(r.street_address),
        },
        zip: {
            header: "Post Code",
            formatter: (_v, r) => naText(r.zip),
        },
        discovery_sources: {
            header: "How did you discover BEAUTY&YOU India?",
            formatter: (_v, r) => naJoin(r.discovery_sources),
        },

        discovery_source_other: {
            header: "How did you discover BEAUTY&YOU India? (Other)",
            formatter: (_v, r) =>
                r.discovery_sources?.includes("Other")
                    ? naText(r.discovery_source_other)
                    : NA,
        },

        ip_address: {
            header: "IP Address",
            formatter: (_v, r) => naText(r.ip_address),
        },
        
        user_agent: {
            header: "User Agent",
            formatter: (_v, r) => naText(r.user_agent),
        },

        // ============================
        // Step 2 - Application Track
        // ============================
        application_track: {
            header: "Selected Track",
            formatter: (_v, r) => naText(formatTrack(r.application_track)),
        },

        primary_category: {
            header: "Primary Category",
            formatter: (_v, r) => {
                if (isBrandTrack(r)) {
                    if (!rHasBrandTrack(r)) return "Not Selected";
                    return rIsGrow(r) ? "Grow" : "Imagine";
                }

                if (isDesignTrack(r)) return "Design";

                if (isBreakthroughTrack(r)) return "Breakthrough";

                return NA;
            },
        },

        // ============================
        // Step 3 - Visionary + Organisation
        // ============================
        visionary_opt_out: {
            header: "Visionary Women's Award Opt Out",
            formatter: (_v, r) => (r.visionary_opt_out === true ? "Yes" : "No"),
        },
        organisation_type: {
            header: "What best describes your organisation type?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.organisation_type) : NA),
        },
        organisation_type_other: {
            header: "What best describes your organisation type? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.organisation_type === "Other"
                    ? naText(r.organisation_type_other)
                    : NA,
        },
        funding_stage: {
            header: "If startup, specify funding stage",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.organisation_type === "Startup (incorporated, under 3 years)"
                    ? naText(r.funding_stage)
                    : NA,
        },
        institution_name: {
            header: "Institution Name",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && isInstitutionOrgType(r) ? naText(r.institution_name) : NA,
        },
        department: {
            header: "Department / Faculty",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && isInstitutionOrgType(r) ? naText(r.department) : NA,
        },
        principal_investigator: {
            header: "Principal Investigator / Lead Researcher Name",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && isInstitutionOrgType(r) ? naText(r.principal_investigator) : NA,
        },
        technology_transfer_involved: {
            header: "Has your institution's technology transfer or commercialisation office been involved in this work?",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && isInstitutionOrgType(r)
                    ? naText(r.technology_transfer_involved)
                    : NA,
        },
        company_registration_date: {
            header: "Date of Company Registration",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && isCompanyOrgType(r) ? naText(r.company_registration_date) : NA,
        },

        // ============================
        // Step 4 - Links & Files
        // ============================
        company_website: {
            header: "Company Website",
            formatter: (_v, r) => naLink(r.company_website),
        },
        linkedin: {
            header: "LinkedIn",
            formatter: (_v, r) => naLink(r.linkedin),
        },
        instagram: {
            header: "Instagram",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.instagram) : NA),
        },
        youtube: {
            header: "YouTube",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.youtube) : NA),
        },
        tiktok: {
            header: "TikTok",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.tiktok) : NA),
        },
        facebook: {
            header: "Facebook",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.facebook) : NA),
        },
        snapchat: {
            header: "Snapchat",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naSnapchat(r.snapchat) : NA),
        },
        other_social: {
            header: "Other Social",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.other_social) : NA),
        },
        research_publications_link: {
            header: "Research Publications",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naLink(r.research_publications_link) : NA),
        },
        other_relevant_link: {
            header: "Other Relevant Link",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naLink(r.other_relevant_link) : NA),
        },
        business_overview_files: {
            header: "Business Overview File",
            formatter: (_v, r) => naFiles(r.business_overview_files, imageUrl),
        },
        intro_video_url: {
            header: "Intro Video",
            formatter: (_v, r) => (isBrandTrack(r) || isDesignTrack(r) ? naLink(r.intro_video_url) : NA),
        },
        founder_headshot_files: {
            header: "Founder Headshots",
            formatter: (_v, r) => naFiles(r.founder_headshot_files, imageUrl),
        },

        // ============================
        // Step 5 - Brand
        // ============================
        trading_status: {
            header: "What's your current trading status?",
            formatter: (_v, r) => {
                if (!isBrandTrack(r)) return NA;

                return naText(r.trading_status)?.replace(/₹/g, "INR ");
            },
        },
        focus_areas: {
            header: "Which best describes your focus area?",
            formatter: (_v, r) => (isBrandTrack(r) ? naJoin(r.focus_areas) : NA),
        },
        focus_area_other: {
            header: "Which best describes your focus area? (Other)",
            formatter: (_v, r) =>
                isBrandTrack(r) && r.focus_areas?.includes("Other") ? naText(r.focus_area_other) : NA,
        },


        // ============================
        // Step 5 - Design
        // ============================
        design_applicant_type: {
            header: "Are you applying as an individual or a collective/studio?",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_applicant_type) : NA),
        },
        collective_members: {
            header: "Who are the members of your collective, and what is each person's role?",
            formatter: (_v, r) =>
                isDesignTrack(r) && r.design_applicant_type === "Collective or studio"
                    ? naText(r.collective_members)
                    : NA,
        },
        creative_name: {
            header: "Full Name / Collective Name",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.creative_name) : NA),
        },
        creative_practices: {
            header: "How would you describe your creative practice?",
            formatter: (_v, r) => (isDesignTrack(r) ? naJoin(r.creative_practices) : NA),
        },
        creative_practice_other: {
            header: "How would you describe your creative practice? (Other)",
            formatter: (_v, r) =>
                isDesignTrack(r) && r.creative_practices?.includes("Other")
                    ? naText(r.creative_practice_other)
                    : NA,
        },
        portfolio_links: {
            header: "Portfolio Link(s)",
            formatter: (_v, r) => (isDesignTrack(r) ? naJoin(r.portfolio_links?.filter(Boolean)) : NA),
        },
        creative_experience: {
            header: "Years working in your creative field",
            formatter: (_v, r) => {
                if (!isDesignTrack(r)) return NA;

                return naText(r.creative_experience)
                    ?.replace(/â€™/g, "'")
                    ?.replace(/â€“/g, "-")
                    ?.replace(/â€”/g, "-")
                    ?.replace(/Â/g, "");
            },
        },
        beauty_projects: {
            header: "Have you previously worked on beauty-adjacent creative projects?",
            formatter: (_v, r) => (isDesignTrack(r) ? naBoolean(r.beauty_projects) : NA),
        },
        beauty_project_description: {
            header: "If yes, briefly describe one example.",
            formatter: (_v, r) =>
                isDesignTrack(r) && r.beauty_projects === true ? naText(r.beauty_project_description) : NA,
        },

        // ============================
        // Step 5 - Breakthrough
        // ============================
        breakthrough_trading_status: {
            header: "Is your organisation currently selling products or services in-market?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.breakthrough_trading_status) : NA),
        },

        // ============================
        // Step 6 - Grow
        // ============================
        grow_elevator_pitch: {
            header: "What is your company's elevator pitch?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_elevator_pitch) : NA),
        },
        grow_problem: {
            header: "What problem are you solving and for whom?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_problem) : NA),
        },
        grow_differentiation: {
            header: "What is genuinely different about your product?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_differentiation) : NA),
        },
        grow_indian_market_opportunity: {
            header: "Who are the closest existing alternatives in the Indian market, and what gap are you filling that they don't?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_indian_market_opportunity) : NA),
        },
        grow_last_full_year_revenue: {
            header: "What were your most recent full-year revenues?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_last_full_year_revenue) : NA),
        },
        grow_revenue_growth: {
            header: "What is your YoY revenue growth rate, and what is driving it?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_revenue_growth) : NA),
        },
        grow_distribution_channels: {
            header: "What are your distribution channels and what % of revenue does each represent?",
            formatter: (_v, r) => {
                if (!isGrowSubTrack(r) || !r.grow_distribution_channels?.length) return NA;
                return r.grow_distribution_channels
                    .map((c) => `${c.channel || NA} (${c.percentage ?? 0}%)`)
                    .join(", ");
            },
        },
        grow_distribution_channels_other: {
            header: "What are your distribution channels and what % of revenue does each represent? (Other)",
            formatter: (_v, r) =>
                isGrowSubTrack(r) && r.grow_distribution_channels?.some((c) => c.channel === "Other")
                    ? naText(r.grow_distribution_channels_other)
                    : NA,
        },
        grow_repeat_purchase_rate: {
            header: "Repeat Purchase Rate (%)",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_repeat_purchase_rate) : NA),
        },
        grow_customer_acquisition_cost: {
            header: "CAC (INR)",
            formatter: (_v, r) =>
                isGrowSubTrack(r) ? naText(r.grow_customer_acquisition_cost) : NA,
        },
        grow_average_order_value: {
            header: "AOV (INR)",
            formatter: (_v, r) =>
                isGrowSubTrack(r) ? naText(r.grow_average_order_value) : NA,
        },
        grow_external_funding: {
            header: "Tell us about any external funding you've received or are actively pursuing.",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naJoin(r.grow_external_funding) : NA),
        },
        grow_external_funding_other: {
            header: "Tell us about any external funding you've received or are actively pursuing. (Other)",
            formatter: (_v, r) =>
                isGrowSubTrack(r) && r.grow_external_funding?.includes("Other")
                    ? naText(r.grow_external_funding_other)
                    : NA,
        },
        grow_founder_story: {
            header: "Why are you the right founder to lead this brand?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_founder_story) : NA),
        },
        grow_team_description: {
            header: "Tell us about your team.",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_team_description) : NA),
        },
        grow_three_year_vision: {
            header: "Where do you see your business in 3 years?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_three_year_vision) : NA),
        },
        grow_business_risks: {
            header:
                "What is the single biggest risk to your business in the next 12-24 months, and how are you thinking about it?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_business_risks) : NA),
        },
        grow_grant_usage: {
            header: "How would you use the BEAUTY&YOU grant funding over the next 12 months?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_grant_usage) : NA),
        },
        grow_ecosystem_support: {
            header: "Beyond funding, what would you most value from the BEAUTY&YOU ecosystem?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naJoin(r.grow_ecosystem_support) : NA),
        },
        grow_ecosystem_support_other: {
            header: "Beyond funding, what would you most value from the BEAUTY&YOU ecosystem? (Other)",
            formatter: (_v, r) =>
                isGrowSubTrack(r) && r.grow_ecosystem_support?.includes("Other")
                    ? naText(r.grow_ecosystem_support_other)
                    : NA,
        },
        grow_win_reason: {
            header: "Why should you win?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_win_reason) : NA),
        },
        grow_product_samples: {
            header:
                "If shortlisted, would you be able to send 3 full sets of product ahead of time to ELC for evaluation?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naBoolean(r.grow_product_samples) : NA),
        },
        grow_additional_information: {
            header: "Anything else you'd like us to know?",
            formatter: (_v, r) => (isGrowSubTrack(r) ? naText(r.grow_additional_information) : NA),
        },

        // ============================
        // Step 6 - Imagine
        // ============================
        imagine_concept: {
            header: "Describe your concept.",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_concept) : NA),
        },
        imagine_consumer_problem: {
            header: "What specific consumer problem are you solving?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_consumer_problem) : NA),
        },
        imagine_core_innovation: {
            header: "Core formulation, ingredient, or format innovation",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_core_innovation) : NA),
        },
        imagine_concept_stage: {
            header: "What stage is the concept at?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_concept_stage) : NA),
        },
        imagine_target_consumer: {
            header: "Who is your target consumer?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_target_consumer) : NA),
        },
        imagine_market_validation: {
            header: "How do you know this consumer exists, and roughly how many of them are there?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_market_validation) : NA),
        },
        imagine_market_gap: {
            header: "Who are the closest existing alternatives in the Indian market, and what gap are you filling that they don't?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_market_gap) : NA),
        },
        imagine_founder_story: {
            header: "Why are you the right person to build this?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_founder_story) : NA),
        },
        imagine_team_description: {
            header: "If you have a team, what experience does each member bring to this concept?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_team_description) : NA),
        },
        imagine_three_year_vision: {
            header: "Where do you see the brand in 3 years?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_three_year_vision) : NA),
        },
        imagine_current_traction: {
            header: "What signals so far suggest this concept resonates?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_current_traction) : NA),
        },
        imagine_sales_stage: {
            header: "Have you sold anything yet?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_sales_stage) : NA),
        },
        imagine_sales_stage_other: {
            header: "Have you sold anything yet? (Other)",
            formatter: (_v, r) =>
                isImagineSubTrack(r) && r.imagine_sales_stage === "Other"
                    ? naText(r.imagine_sales_stage_other)
                    : NA,
        },
        // imagine_sales_details: {
        //     header: "If yes, please share details (channels, monthly sales, etc.)",
        //     formatter: (_v, r) => {
        //         const qualifyingStages = [
        //             "Yes — small-batch or limited sales",
        //             "Yes — but very early / pre-orders only",
        //             "Other",
        //         ];
        //         return isImagineSubTrack(r) && qualifyingStages.includes(r.imagine_sales_stage)
        //             ? naText(r.imagine_sales_details)
        //             : NA;
        //     },
        // },
        imagine_external_funding: {
            header:
                "Tell us about any external funding you've received or are actively pursuing for this concept.",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naJoin(r.imagine_external_funding) : NA),
        },
        imagine_external_funding_other: {
            header:
                "Tell us about any external funding you've received or are actively pursuing for this concept. (Other)",
            formatter: (_v, r) =>
                isImagineSubTrack(r) && r.imagine_external_funding?.includes("Other")
                    ? naText(r.imagine_external_funding_other)
                    : NA,
        },
        // imagine_external_funding_details: {
        //     header: "Provide details on funding amount, investor name, and date of close",
        //     formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_external_funding_details) : NA),
        // },
        imagine_biggest_uncertainty: {
            header:
                "What are you most uncertain about right now, and what would you want to learn or de-risk first?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_biggest_uncertainty) : NA),
        },
        imagine_grant_usage: {
            header: "How would you use the BEAUTY&YOU grant funding over the next 12 months?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_grant_usage) : NA),
        },
        imagine_ecosystem_support: {
            header: "Beyond funding, what would you most value from the BEAUTY&YOU ecosystem?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naJoin(r.imagine_ecosystem_support) : NA),
        },
        imagine_ecosystem_support_other: {
            header: "Beyond funding, what would you most value from the BEAUTY&YOU ecosystem? (Other)",
            formatter: (_v, r) =>
                isImagineSubTrack(r) && r.imagine_ecosystem_support?.includes("Other")
                    ? naText(r.imagine_ecosystem_support_other)
                    : NA,
        },
        imagine_win_reason: {
            header: "Why should you win?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_win_reason) : NA),
        },
        imagine_additional_information: {
            header: "Anything else you'd like us to know?",
            formatter: (_v, r) => (isImagineSubTrack(r) ? naText(r.imagine_additional_information) : NA),
        },

        // ============================
        // Step 6 - Design
        // ============================
        design_portfolio_files: {
            header: "Upload 5-10 pieces of your strongest work",
            formatter: (_v, r) => (isDesignTrack(r) ? naFiles(r.design_portfolio_files, imageUrl) : NA),
        },
        design_portfolio_story: {
            header:
                "Pick one project from your portfolio and tell us the story behind it - the idea, your role, and the outcome.",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_portfolio_story) : NA),
        },
        design_public_work: {
            header:
                "If your work has appeared in any campaign, publication, exhibition, or product that reached the public, tell us about it.",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_public_work) : NA),
        },
        design_future_of_beauty: {
            header:
                "What is your point of view on the future of beauty in India? Where is it going, and what's missing?",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_future_of_beauty) : NA),
        },
        design_cultural_idea: {
            header:
                "Share one idea for how a beauty brand could create a culturally relevant moment in India - a collection, packaging concept, pop-up, or experience.",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_cultural_idea) : NA),
        },
        design_cultural_inspirations: {
            header: "What Indian cultural references, crafts, traditions, or communities inspire your work?",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_cultural_inspirations) : NA),
        },
        design_moodboard_files: {
            header: "Upload a moodboard or visual that brings your idea in C2 to life",
            formatter: (_v, r) => (isDesignTrack(r) ? naFiles(r.design_moodboard_files, imageUrl) : NA),
        },
        design_brand_collaboration: {
            header: "Have you collaborated with a brand or client before? Briefly describe one example.",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_brand_collaboration) : NA),
        },
        design_finalist_availability: {
            header:
                "If selected as a finalist, you'll be given a creative brief and around 3-4 weeks to develop a response. Do you confirm you'd be available for this and for an in-person finalist presentation in September 2026?",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_finalist_availability) : NA),
        },
        design_working_setup: {
            header: "Is there anything we should know about your capacity or working setup?",
            formatter: (_v, r) => (isDesignTrack(r) ? naText(r.design_working_setup) : NA),
        },

        // ============================
        // Step 6 - Breakthrough
        // ============================
        breakthrough_technical_overview_files: {
            header: "One-page technical overview of your innovation",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naFiles(r.breakthrough_technical_overview_files, imageUrl) : NA,
        },
        breakthrough_optional_video_url: {
            header: "Optional video link",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naLink(r.breakthrough_optional_video_url) : NA),
        },
        breakthrough_safety_data_sheet_files: {
            header: "Safety Data Sheet",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naFiles(r.breakthrough_safety_data_sheet_files, imageUrl) : NA,
        },
        breakthrough_patent_reference_files: {
            header: "Patent Reference",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naFiles(r.breakthrough_patent_reference_files, imageUrl) : NA,
        },
        breakthrough_scientific_publication_files: {
            header: "Scientific publications, technical papers, or research documentation",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naFiles(r.breakthrough_scientific_publication_files, imageUrl) : NA,
        },
        breakthrough_technology_types: {
            header: "What best describes the technology type of your innovation?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_technology_types) : NA),
        },
        breakthrough_technology_type_other: {
            header: "What best describes the technology type of your innovation? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_technology_types?.includes("Other")
                    ? naText(r.breakthrough_technology_type_other)
                    : NA,
        },
        breakthrough_primary_application_areas: {
            header: "What best describes the primary application area of your innovation?",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naJoin(r.breakthrough_primary_application_areas) : NA,
        },
        breakthrough_primary_application_area_other: {
            header: "What best describes the primary application area of your innovation? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_primary_application_areas?.includes("Other")
                    ? naText(r.breakthrough_primary_application_area_other)
                    : NA,
        },
        breakthrough_trl: {
            header: "What is the Technology Readiness Level (TRL) of your innovation?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.breakthrough_trl) : NA),
        },
        breakthrough_innovation_scope: {
            header: "How would you best describe the scope of your innovation?",
            formatter: (_v, r) => {
                if (!isBreakthroughTrack(r)) return NA;

                const scope = r.breakthrough_innovation_scope;

                return scope?.toLowerCase() === "both"
                    ? "Both - Developed specifically for fragrance application; Enabling / platform technology applicable across multiple sectors"
                    : naText(scope);
            },
        },
        breakthrough_formulation_properties: {
            header:
                "Which of the following describe your innovation's known fragrance compatibility or formulation properties?",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naJoin(r.breakthrough_formulation_properties) : NA,
        },
        breakthrough_formulation_property_other: {
            header:
                "Which of the following describe your innovation's known fragrance compatibility or formulation properties? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_formulation_properties?.includes("Other")
                    ? naText(r.breakthrough_formulation_property_other)
                    : NA,
        },
        breakthrough_fragrance_benefits: {
            header: "Which of the following fragrance benefits does your innovation address or enable?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_fragrance_benefits) : NA),
        },
        breakthrough_fragrance_benefit_other: {
            header:
                "Which of the following fragrance benefits does your innovation address or enable? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_fragrance_benefits?.includes("Other")
                    ? naText(r.breakthrough_fragrance_benefit_other)
                    : NA,
        },
        breakthrough_solution_comparison: {
            header: "How does your approach compare to existing solutions?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_solution_comparison) : NA),
        },
        breakthrough_solution_comparison_other: {
            header: "How does your approach compare to existing solutions? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_solution_comparison?.includes("Other")
                    ? naText(r.breakthrough_solution_comparison_other)
                    : NA,
        },
        breakthrough_solution_comparison_description: {
            header: "Briefly describe how your approach compares:",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naText(r.breakthrough_solution_comparison_description) : NA,
        },
        breakthrough_supporting_evidence: {
            header: "What evidence do you have that your innovation works?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_supporting_evidence) : NA),
        },
        breakthrough_supporting_evidence_other: {
            header: "What evidence do you have that your innovation works? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_supporting_evidence?.includes("Other")
                    ? naText(r.breakthrough_supporting_evidence_other)
                    : NA,
        },
        breakthrough_consumer_needs: {
            header: "What end-user or consumer need does your innovation ultimately address?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_consumer_needs) : NA),
        },
        breakthrough_consumer_need_other: {
            header: "What end-user or consumer need does your innovation ultimately address? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_consumer_needs?.includes("Other")
                    ? naText(r.breakthrough_consumer_need_other)
                    : NA,
        },
        // breakthrough_consumer_need_description: {
        //     header: "Briefly describe how your approach compares:",
        //     formatter: (_v, r) =>
        //         isBreakthroughTrack(r) ? naText(r.breakthrough_consumer_need_description) : NA,
        // },
        breakthrough_prototype_status: {
            header: "What is the current status of your prototype, sample, or demonstration material?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.breakthrough_prototype_status) : NA),
        },
        breakthrough_ip_status: {
            header: "What is the IP status of your innovation?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.breakthrough_ip_status) : NA),
        },
        breakthrough_ip_reference_details: {
            header: "Patent Reference Number / Details:",
            formatter: (_v, r) => {
                const qualifyingStatuses = ["Granted patent(s)", "Patent application(s) filed, pending"];
                return isBreakthroughTrack(r) && qualifyingStatuses.includes(r.breakthrough_ip_status)
                    ? naText(r.breakthrough_ip_reference_details)
                    : NA;
            },
        },
        breakthrough_registered_regions: {
            header:
                "In which regions is your innovation registered, approved, cleared, or actively seeking registration?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_registered_regions) : NA),
        },
        breakthrough_registered_region_other: {
            header:
                "In which regions is your innovation registered, approved, cleared, or actively seeking registration? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_registered_regions?.includes("Other")
                    ? naText(r.breakthrough_registered_region_other)
                    : NA,
        },
        breakthrough_has_publications: {
            header:
                "Do you have scientific publications, technical papers, or research documentation supporting your innovation?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naBoolean(r.breakthrough_has_publications) : NA),
        },
        breakthrough_publications_details: {
            header: "If Yes, list titles, journals, DOIs, or URLs:",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_has_publications === true
                    ? naText(r.breakthrough_publications_details)
                    : NA,
        },
        breakthrough_collaboration_preferences: {
            header:
                "Are you open to exclusivity or a preferential collaboration arrangement with a single partner organisation?",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naJoin(r.breakthrough_collaboration_preferences) : NA,
        },
        breakthrough_team_background: {
            header: "Briefly describe your team background",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naText(r.breakthrough_team_background) : NA),
        },
        breakthrough_funding_support: {
            header: "What funding or institutional support have you received to date?",
            formatter: (_v, r) => (isBreakthroughTrack(r) ? naJoin(r.breakthrough_funding_support) : NA),
        },
        breakthrough_funding_support_other: {
            header: "What funding or institutional support have you received to date? (Other)",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_funding_support?.includes("Other")
                    ? naText(r.breakthrough_funding_support_other)
                    : NA,
        },
        breakthrough_has_corporate_collaboration: {
            header:
                "Have you previously collaborated, or are you in active collaboration, with a large corporate organisation on a technology development, licensing, or pilot project?",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) ? naBoolean(r.breakthrough_has_corporate_collaboration) : NA,
        },
        breakthrough_corporate_collaboration_details: {
            header:
                "If Yes, briefly describe (type of organisation, nature of collaboration). You do not need to name the organisation.",
            formatter: (_v, r) =>
                isBreakthroughTrack(r) && r.breakthrough_has_corporate_collaboration === true
                    ? naText(r.breakthrough_corporate_collaboration_details)
                    : NA,
        },

        // ============================
        // Step 7 - Visionary Women's Award
        // ============================
        visionary_founder_story: {
            header:
                "Tell us about your founder story — What inspired you to build this business, and how have your experiences as a woman founder shaped the kind of leader you are today?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_founder_story) : NA),
        },
        visionary_leadership_milestone: {
            header:
                "What is one breakthrough or milestone in your leadership journey that you're especially proud of, and what did it take to achieve it?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_leadership_milestone) : NA),
        },
        visionary_women_consumer_challenge: {
            header:
                "How does your business address unmet needs or challenges faced by women consumers in beauty, and what unique insight or lived experience do you bring to solving these issues?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_women_consumer_challenge) : NA),
        },
        visionary_women_empowerment: {
            header:
                "How is your business creating opportunities or advancing progress for other women — through your team, supply chain, partnerships, community, or broader beauty industry?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_women_empowerment) : NA),
        },
        visionary_business_attributes: {
            header: "Which of the following apply to your business?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naJoin(r.visionary_business_attributes) : NA),
        },
        visionary_three_year_vision: {
            header:
                "What is your vision for your business in 3 years, and how would the Visionary Women's Award and BEAUTY&YOU platform help accelerate that growth and impact?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_three_year_vision) : NA),
        },
        visionary_additional_information: {
            header:
                "Is there anything else you'd like us to know about yourself, your business, or the impact you hope to create as a woman founder?",
            formatter: (_v, r) => (isVisionaryEligible(r) ? naText(r.visionary_additional_information) : NA),
        },

        // ============================
        // Status and timestamps
        // ============================
        status: {
            header: "Status",
            formatter: (_v, r) =>
                r.status === "registered" ? r.status : r.status === "completed" ? r.status : "partially filled",
        },
        createdAt: {
            header: "Created At",
            formatter: (_v, r) => naText(helper.ISTDate(r.createdAt)),
        },
        updatedAt: {
            header: "Completed At",
            formatter: (_v, r) => naText(helper.ISTDate(r.updatedAt)),
        },
    };
};